// Utility functions for parsing and detecting artifacts

/**
 * Parses artifact tags from text and extracts artifact data
 * @param {string} text - The text containing potential artifact tags
 * @returns {Object} - { artifacts: Array, cleanText: string }
 */
export const parseArtifacts = (text) => {
  const artifacts = [];
  const artifactRegex = /<artifact\s+([^>]+)>([\s\S]*?)<\/artifact>/g;
  
  let cleanText = text;
  let match;
  
  while ((match = artifactRegex.exec(text)) !== null) {
    const attributesString = match[1];
    const content = match[2].trim();
    
    // Parse attributes
    const attributes = parseArtifactAttributes(attributesString);
    
    // Create artifact object
    const artifact = {
      id: generateArtifactId(),
      type: attributes.type || 'text',
      title: attributes.title || 'Untitled Artifact',
      language: attributes.language || null,
      content: content
    };
    
    artifacts.push(artifact);
    
    // Remove artifact tag from clean text and replace with unique placeholder
    cleanText = cleanText.replace(match[0], `[ARTIFACT_PLACEHOLDER_${artifacts.length - 1}]`);
  }
  
  return { artifacts, cleanText };
};

/**
 * Parses artifact attributes from the attribute string
 * @param {string} attributesString - String containing HTML-like attributes
 * @returns {Object} - Parsed attributes object
 */
const parseArtifactAttributes = (attributesString) => {
  const attributes = {};
  const attrRegex = /(\w+)=["']([^"']+)["']/g;
  
  let match;
  while ((match = attrRegex.exec(attributesString)) !== null) {
    attributes[match[1]] = match[2];
  }
  
  return attributes;
};

/**
 * Detects if text contains artifact tags during streaming
 * @param {string} text - Accumulated text so far
 * @returns {boolean} - True if artifact tags are detected
 */
export const hasArtifactTags = (text) => {
  return /<artifact\s+[^>]*>/i.test(text);
};

/**
 * Detects if an artifact is complete in the streaming text
 * @param {string} text - Accumulated text so far
 * @returns {boolean} - True if at least one complete artifact is found
 */
export const hasCompleteArtifact = (text) => {
  const artifactRegex = /<artifact\s+[^>]*>[\s\S]*?<\/artifact>/gi;
  return artifactRegex.test(text);
};

/**
 * Extracts the last incomplete artifact from streaming text
 * @param {string} text - Accumulated text so far
 * @returns {Object|null} - Partial artifact data or null
 */
export const getIncompleteArtifact = (text) => {
  // Find opening artifact tag without closing
  const openTagRegex = /<artifact\s+([^>]+)>([\s\S]*)$/i;
  const match = openTagRegex.exec(text);
  
  if (!match) return null;
  
  const attributes = parseArtifactAttributes(match[1]);
  const partialContent = match[2];
  
  // Check if there's a closing tag in the partial content
  if (partialContent.includes('</artifact>')) return null;
  
  return {
    id: 'streaming-artifact',
    type: attributes.type || 'text',
    title: attributes.title || 'Untitled Artifact',
    language: attributes.language || null,
    content: partialContent,
    isStreaming: true
  };
};

/**
 * Generates a unique ID for artifacts
 * @returns {string} - Unique artifact ID
 */
const generateArtifactId = () => {
  return 'artifact-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
};

/**
 * Renders text with inline artifacts as React components
 * @param {string} text - Text with artifact placeholders
 * @param {Array} artifacts - Array of artifact objects
 * @param {Function} ArtifactCardComponent - Component to render artifacts
 * @returns {Array} - Array of React elements
 */
export const renderTextWithInlineArtifacts = (text, artifacts, ArtifactCardComponent) => {
  const parts = [];
  let partIndex = 0;

  // Split text by artifact placeholders
  const placeholderRegex = /\[ARTIFACT_PLACEHOLDER_(\d+)\]/g;
  let lastIndex = 0;
  let match;

  while ((match = placeholderRegex.exec(text)) !== null) {
    // Add text before the placeholder
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index);
      if (textBefore.trim()) {
        parts.push({
          type: 'text',
          content: textBefore,
          key: `text-${partIndex++}`
        });
      }
    }

    // Add the artifact
    const artifactIndex = parseInt(match[1], 10);
    if (artifacts[artifactIndex]) {
      parts.push({
        type: 'artifact',
        content: artifacts[artifactIndex],
        key: `artifact-${partIndex++}`
      });
    }

    lastIndex = placeholderRegex.lastIndex;
  }

  // Add remaining text after the last placeholder
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText.trim()) {
      parts.push({
        type: 'text',
        content: remainingText,
        key: `text-${partIndex++}`
      });
    }
  }

  return parts;
};

/**
 * Simple function to render message with inline artifacts
 * @param {string} messageText - Full message text
 * @param {Function} onArtifactOpen - Callback for opening artifacts  
 * @param {Function} ArtifactCardComponent - Component to render artifacts
 * @param {Function} MarkdownComponent - Component to render markdown
 * @returns {Array} - Array of React elements
 */
export const renderMessageWithInlineArtifacts = (messageText, onArtifactOpen, ArtifactCardComponent, MarkdownComponent) => {
  const { artifacts, cleanText } = parseArtifacts(messageText);
  
  if (artifacts.length === 0) {
    return [MarkdownComponent(messageText)];
  }

  const parts = renderTextWithInlineArtifacts(cleanText, artifacts);
  
  return parts.map((part) => {
    if (part.type === 'text') {
      return MarkdownComponent(part.content, part.key);
    } else if (part.type === 'artifact' && onArtifactOpen && ArtifactCardComponent) {
      return ArtifactCardComponent(part.content, part.key, onArtifactOpen);
    }
    return null;
  }).filter(Boolean);
};

/**
 * Processes streaming content and returns artifact state
 * @param {string} streamingText - Current accumulated text
 * @returns {Object} - { hasArtifact: boolean, currentArtifact: Object|null, cleanText: string }
 */
export const processStreamingContent = (streamingText) => {
  // Check if we have any artifact tags
  if (!hasArtifactTags(streamingText)) {
    return {
      hasArtifact: false,
      currentArtifact: null,
      cleanText: streamingText
    };
  }
  
  // During streaming, we need to preserve text order but remove artifact content
  // Replace complete artifacts with placeholder markers during streaming to preserve text flow
  
  const artifacts = [];
  const artifactRegex = /<artifact\s+([^>]+)>([\s\S]*?)<\/artifact>/g;
  let cleanText = streamingText;
  let match;
  
  while ((match = artifactRegex.exec(streamingText)) !== null) {
    const attributesString = match[1];
    const content = match[2].trim();
    
    // Parse attributes
    const attributes = parseArtifactAttributes(attributesString);
    
    // Create artifact object
    const artifact = {
      id: generateArtifactId(),
      type: attributes.type || 'text',
      title: attributes.title || 'Untitled Artifact',
      language: attributes.language || null,
      content: content
    };
    
    artifacts.push(artifact);
    
    // Replace artifact with placeholder during streaming to preserve text order
    cleanText = cleanText.replace(match[0], `[STREAMING_ARTIFACT_${artifacts.length - 1}]`);
  }
  
  // Then, remove any incomplete artifacts that might still be at the end
  const partialRegex = /<artifact\s+[^>]*>[\s\S]*$/i;
  const fullyCleanText = cleanText.replace(partialRegex, '').trim();
  
  // Check for incomplete/streaming artifact
  const incompleteArtifact = getIncompleteArtifact(streamingText);
  
  if (incompleteArtifact) {
    return {
      hasArtifact: true,
      currentArtifact: incompleteArtifact,
      cleanText: fullyCleanText,
      allArtifacts: artifacts, // Include complete artifacts if any
      hasInlinePlaceholders: /\[STREAMING_ARTIFACT_\d+\]/.test(fullyCleanText)
    };
  }
  
  // If we have complete artifacts during streaming, return all of them for inline rendering
  if (artifacts.length > 0) {
    const lastArtifact = artifacts[artifacts.length - 1];
    return {
      hasArtifact: true,
      currentArtifact: {
        ...lastArtifact,
        isStreaming: true // Force streaming state to show "Working..." card
      },
      cleanText: fullyCleanText, // Use the clean text with placeholders for inline rendering
      allArtifacts: artifacts, // Include all artifacts for inline rendering
      hasInlinePlaceholders: /\[STREAMING_ARTIFACT_\d+\]/.test(fullyCleanText)
    };
  }
  
  return {
    hasArtifact: false,
    currentArtifact: null,
    cleanText: fullyCleanText
  };
};